import { useEffect, useState } from 'react'
import { Alert, Form, Modal, Space, Typography } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { ContactFields } from '@/features/contact/ContactFields'
import {
  buildContactRequest,
  createContact,
  updateContact,
  type ContactFieldValues,
} from '@/features/contact/contactApi'
import { useOwnerContact } from '@/features/contact/useOwnerContact'
import { SubscriberFormFields, type SubscriberFieldValues } from './SubscriberFormFields'
import { SubscriberProfileFields } from './SubscriberProfileFields'
import { profileDraft, profileFormValues, type ProfileFieldValues } from './profileForm'
import { normalizePhone, updateSubscriber, type Subscriber } from './subscriberApi'
import { useSubscriberPhoneReveal } from './useSubscriberPhoneReveal'
import { ContactRevealControl } from '@/features/contact/ContactRevealControl'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface EditSubscriberModalProps {
  open: boolean
  subscriber: Subscriber
  onClose: () => void
}

interface EditFormValues extends SubscriberFieldValues {
  contact?: ContactFieldValues
  profile?: ProfileFieldValues
}

/**
 * Abonent maydonlarini tahrirlash.
 *
 * <h2>Nima o'zgaradi va nima o'zgarmaydi</h2>
 * Forma `displayName`, `phone`, `kind`, `simultaneousUse` va `staticIp` ni
 * oladi. Login va parol bu yerda YO'Q — sabablari `SubscriberFormFields` da.
 *
 * ⚠️ Ko'rinmaydigan, lekin MAJBURIY qism: `ownerPartyId` mavjud
 * abonentdan olinib o'zgarishsiz qaytariladi. Backend `PUT` ni
 * to'liq almashtirish deb bajaradi (`subscriberApi.SubscriberDraft`
 * javadoc'i), ya'ni uni yubormaslik abonentni egasidan uzib qo'yardi
 * va u agent doirasidagi hech kimga ko'rinmay qolardi — xatosiz, jimgina.
 *
 * ⚠️ `agentId` esa endi KO'RINADI va TAHRIRLANADI — mavjud abonentga
 * agent biriktirish/almashtirishning yagona yo'li shu. Sabab: `agent_id`
 * filial daromadi zanjirining birinchi bo'g'ini va u Staging'da 74
 * abonentning hammasida bo'sh edi. Maydon bo'sh qoldirilsa eski qiymat
 * saqlanadi (backend `COALESCE`), shuning uchun tozalash tugmasi ham
 * ataylab YO'Q (`AgentSelect.clearable`).
 *
 * <h2>Natija "saqlandi" degan bilan tugamaydi</h2>
 * `simultaneousUse` `radcheck` ga, tur esa siyosat tanlashiga ta'sir
 * qiladi va ular RADIUS'ga PROYEKSIYA orqali, bir necha soniyada
 * ko'chadi. Bundan ham muhimi — o'zgarish HOZIRGI faol sessiyaga
 * qo'llanmaydi (CLAUDE.md §3.2, Qoida 4). Shuning uchun muvaffaqiyat
 * xabari shuni ochiq aytadi: "saqlandi" deb qoldirish operatorga
 * o'zgarish darhol kuchga kirdi degan yolg'on taassurot berardi.
 */
export function EditSubscriberModal({ open, subscriber, onClose }: EditSubscriberModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<EditFormValues>()
  const queryClient = useQueryClient()

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [contactError, setContactError] = useState<string | null>(null)
  const [contactFilled, setContactFilled] = useState(false)

  const contactState = useOwnerContact('SUBSCRIBER', subscriber.id, open)

  /*
    ⛔ ABONENTNING O'Z RAQAMI — KONTAKT REESTRIDAN ALOHIDA HOLAT.

    `useOwnerContact.locked` kontakt REESTRIDAGI yozuvni qo'riqlaydi;
    bu esa `subscriber.subscriber.phone` ni. Ikkalasi boshqa ustun,
    boshqa endpoint va boshqa ochish yo'li — bittasini ikkinchisiga
    qo'shish bir blokning qulfi ikkinchisini ham ochib yuboradigan
    holatga olib kelardi.
  */
  const phoneReveal = useSubscriberPhoneReveal(subscriber.id, subscriber.phone ?? null, open)

  // Modal har OCHILGANDA qiymatlar serverdagi holatdan qayta to'ldiriladi:
  // oldingi bekor qilingan tahrir ekranda qolib ketmasin.
  //
  // ⚠️ `subscriber` BOG'LIQLIKLARDA YO'Q va bu ataylab. Saqlashdan keyin
  // kesh bekor qilinadi, kartochka qayta so'raladi va `subscriber` YANGI
  // obyekt bo'lib keladi — u bog'liqlikda tursa effekt qayta ishga tushib
  // `saved` ni darhol `false` ga qaytarardi, ya'ni "saqlandi"
  // ogohlantirishi ko'rinishi bilan yo'qolardi. Jonli sinovda aynan shu
  // holat kuzatildi: baza yangilandi, ekranda esa hech nima o'zgarmagandek
  // ko'rinardi.
  useEffect(() => {
    if (!open) return
    setError(null)
    setSaved(false)
    setContactError(null)
    setContactFilled(false)
    form.setFieldsValue({
      login: subscriber.login,
      displayName: subscriber.displayName ?? '',
      phone: subscriber.phone ?? '',
      kind: subscriber.kind,
      // ⚠️ Joriy ega formaga QAYTA YUKLANADI. Usiz maydon bo'sh
      // ko'rinardi va operator «biriktirilmagan» degan YOLG'ONNI
      // o'qirdi — abonentda agent bor bo'lsa ham.
      agentId: subscriber.agentId ?? null,
      simultaneousUse: subscriber.simultaneousUse ?? null,
      staticIp: subscriber.staticIp ?? '',
      profile: profileFormValues(subscriber.profile),
    })
    // ⚠️ `subscriber` ATAYLAB yo'q. Oyna ochiq turganda ro'yxat
    //    qayta yuklansa (refetch) shakl QAYTA TO'LDIRILMASLIGI kerak —
    //    aks holda operator yozayotgan qiymat uning ostidan almashardi
    //    va u buni sezmasdi. Shakl faqat OCHILGANDA to'ldiriladi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form])

  /*
    Kontakt ALOHIDA so'rov bilan keladi, ya'ni oyna ochilganda u hali
    yo'q. To'ldirish BIR MARTA bo'ladi (`contactFilled`) — aks holda
    fon yangilanishi operator yozayotgan manzilni ustidan almashtirardi.
  */
  useEffect(() => {
    if (!open || contactFilled || contactState.loading || !contactState.canRead) return
    form.setFieldsValue({ contact: contactState.values })
    setContactFilled(true)
  }, [open, contactFilled, contactState.loading, contactState.canRead, contactState.values, form])

  // Raqam ochilgandan keyin formadagi NIQOB haqiqiy qiymat bilan
  // almashadi — usiz «ochdim» degan amal hech narsani o'zgartirmasdi.
  useEffect(() => {
    const revealed = contactState.revealed
    if (revealed === null) return
    form.setFieldValue(['contact', 'phone'], revealed.phone ?? '')
    form.setFieldValue(['contact', 'phoneAlt'], revealed.phoneAlt ?? '')
  }, [contactState.revealed, form])

  /*
    ⛔ ABONENT RAQAMI OCHILGACH, FORMADAGI NIQOB HAQIQIY QIYMAT BILAN
    ALMASHADI — usiz «ochdim» degan amal saqlashga hech narsa bermasdi
    va maydon niqob bilan qolib ketardi (`contact` bloki bilan bir xil
    naqsh, yuqoridagi effekt).
  */
  useEffect(() => {
    if (!phoneReveal.revealed) return
    form.setFieldValue('phone', phoneReveal.shown ?? '')
  }, [phoneReveal.revealed, phoneReveal.shown, form])

  /**
   * Kontaktni saqlaydi. `null` — yozadigan narsa yo'q yoki saqlandi.
   *
   * ⚠️ So'rov tanasi `buildContactRequest` da quriladi va faqat o'sha
   * yerda: backendda abonent+kontakt zanjiri hali yozilyapti.
   */
  const saveContact = async (values: ContactFieldValues | undefined): Promise<string | null> => {
    // ⚠️ QULFLANGAN holatda so'rov UMUMAN yuborilmaydi: maydonlar
    // o'chirilgan, ya'ni operator hech narsa o'zgartira olmagan, va
    // niqoblangan raqamni qaytarib yuborish haqiqiy raqamni o'chirardi.
    if (!contactState.canWrite || contactState.locked) return null
    const request = buildContactRequest({
      ownerType: 'SUBSCRIBER',
      ownerId: subscriber.id,
      values: values ?? {},
      current: contactState.contact,
    })
    if (request.kind === 'none') return null
    if (request.kind === 'blocked') {
      return request.reason === 'EMPTY' ? t('contact.emptyBlocked') : t('contact.maskedBlocked')
    }
    try {
      if (request.kind === 'create') await createContact(request.body)
      else await updateContact(request.id, request.body)
      void queryClient.invalidateQueries({
        queryKey: queryKeys.contacts.byOwner('SUBSCRIBER', subscriber.id),
      })
      return null
    } catch (err) {
      return t('contact.updateFailed', { reason: translateError(err, t) })
    }
  }

  const handleSubmit = () => {
    /*
      ⛔ QULFLANGAN RAQAM BILAN SAQLASH UMUMAN BOSHLANMAYDI.

      `PUT /api/subscribers/{id}` — to'liq almashtirish, va `phone`
      backendda `COALESCE` SIZ yoziladi (`SubscriberRepository.update`
      dagi ochiq izoh). Ya'ni bu holatda ikki yo'lning IKKALASI HAM
      ma'lumot yo'qotardi:
        · niqobni yuborish   → raqam `+9989****567` bo'lib qolardi;
        · bo'sh yuborish     → raqam O'CHIRILARDI.
      Uchinchi yo'l — «maydonni yubormaslik = o'zgartirmaslik» — bu
      endpointda YO'Q, chunki `COALESCE` ataylab qo'yilmagan.

      Shuning uchun yagona to'g'ri javob: avval raqamni OCHISH. Xabar
      buni aytadi, `contact` blokidagi `maskedBlocked` bilan bir xil
      uslubda.

      ⚠️ Bu BIRINCHI qavat (operator tushunarli xabar ko'rsin).
      Ikkinchisi `updateSubscriber` ichida (`MaskedPhoneError`) — hech
      bir chaqiruvchi uni chetlab o'tolmasin. Serverda uchinchisi bor.
    */
    if (phoneReveal.locked) {
      setError(t('subscriber.edit.phoneMaskedBlocked'))
      return
    }

    submitOnValid(form, async (values) => {
      setSubmitting(true)
      setError(null)
      setContactError(null)
      try {
        await updateSubscriber(subscriber.id, {
          // Login o'zgarmaydi — maydon o'chirilgan, qiymat shu yerdan qaytadi
          login: subscriber.login,
          displayName: values.displayName?.trim() || null,
          // ⚠️ HAR SAFAR yuboriladi va bu MAJBURIY: backend `phone` ni
          // `COALESCE` siz yozadi, ya'ni yubormaslik raqamni o'chirardi.
          // `agentId` bilan chalkashtirmang — u formada BOR, lekin
          // backendda `COALESCE` bilan yoziladi: bo'sh qiymat egani
          // O'CHIRMAYDI. Ikki maydon, ikki xil qoida — va farq ataylab
          // (`SubscriberDraft` javadoc'i, «phone — tahrirlashda
          // TOZALASH mumkin»).
          phone: normalizePhone(values.phone),
          kind: values.kind,
          // ⚠️ Egalik — o'zgarishsiz qaytariladi. Sinf javadoc'iga qarang.
          ownerPartyId: subscriber.ownerPartyId ?? null,
          // ⚠️ ENDI FORMADAN keladi, `subscriber.agentId` dan EMAS: agent
          // biriktirish va almashtirish aynan shu yo'ldan bajariladi.
          // Zaxira qiymat baribir mavjud abonentniki — maydon bo'sh
          // qolsa (huquq yo'q, ro'yxat yuklanmadi) bog'lanish
          // saqlanadi, `COALESCE` esa uni ustidan yozmaydi.
          agentId: values.agentId ?? subscriber.agentId ?? null,
          simultaneousUse: values.simultaneousUse ?? null,
          staticIp: values.staticIp?.trim() || null,
          profile: profileDraft(values.profile ?? {}),
        })

        void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.detail(subscriber.id) })
        void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
        setSaved(true)

        // ⚠️ Kontakt BOSHQA SERVISDA (ADR-016) — umumiy tranzaksiya yo'q.
        // Uning yiqilishi abonent saqlanganini bekor qilmaydi, lekin
        // JIMGINA ham yutilmaydi: sabab shu oynada ko'rinadi.
        setContactError(await saveContact(values.contact))
      } catch (err) {
        setError(translateError(err, t))
      } finally {
        setSubmitting(false)
      }
    })
  }

  return (
    <Modal
      open={open}
      title={t('subscriber.edit.title')}
      onCancel={onClose}
      onOk={saved ? onClose : handleSubmit}
      okText={saved ? t('common.close') : t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={submitting}
      destroyOnHidden
      width={760}
    >
      <Typography.Paragraph type="secondary">
        {t('subscriber.edit.subtitle', { login: subscriber.login })}
      </Typography.Paragraph>

      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      ) : null}

      {contactError ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('contact.saveFailedTitle')}
          description={contactError}
        />
      ) : null}

      {contactState.error ? (
        <Alert type="error" showIcon message={contactState.error} style={{ marginBottom: 16 }} />
      ) : null}

      {saved ? (
        // ⚠️ ATAYLAB `success` EMAS, `warning`: baza yangilandi, lekin faol
        // sessiyaga qo'llanmadi. Yashil belgi "hammasi tayyor" degan
        // ma'noni berardi va operator abonentga shunday aytardi.
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('subscriber.edit.savedTitle')}
          description={t('subscriber.edit.savedHint')}
        />
      ) : (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('subscriber.edit.radiusNote')}
        />
      )}

      <Form<EditFormValues> form={form} layout="vertical" requiredMark disabled={submitting || saved}>
        <SubscriberProfileFields mode="edit" current={subscriber.profile} />

        <SubscriberFormFields
          mode="edit"
          phoneLocked={phoneReveal.locked}
          showDisplayName={!subscriber.profile?.firstName && !subscriber.profile?.lastName}
        />

        {/*
          ⛔ QULFNING SABABI VA UNDAN CHIQISH YO'LI — MAYDONNING
          O'ZI YONIDA. O'chirilgan maydonni sababsiz ko'rsatish
          operatorni «forma buzuq» degan xulosaga olib borardi.

          ⚠️ Huquq yo'q bo'lsa tugma UMUMAN chizilmaydi, o'rniga sabab
          yoziladi (`ContactCard` naqshi): bosilganda 403 beradigan
          tugma bu kod bazasida rad etilgan.
        */}
        {phoneReveal.locked ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('subscriber.edit.phoneMaskedTitle')}
            description={
              phoneReveal.canReveal ? (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  <span>{t('subscriber.edit.phoneMaskedHint')}</span>
                  <ContactRevealControl
                    onReveal={phoneReveal.reveal}
                    revealing={phoneReveal.revealing}
                    revealError={phoneReveal.revealError}
                  />
                </Space>
              ) : (
                t('subscriber.edit.phoneMaskedNoReveal')
              )
            }
          />
        ) : null}

        {/*
          ⚠️ `canRead` bo'lmasa blok UMUMAN chizilmaydi: serverdan kontakt
          kelmaydi, ya'ni maydonlar BO'SH bo'lardi va saqlash mavjud
          ma'lumotni «o'chirildi» deb yozib yuborardi.
        */}
        {contactState.canRead ? (
          <ContactFields
            group="contact"
            loading={contactState.loading}
            missing={contactState.missing}
            locked={contactState.locked}
            readOnly={!contactState.canWrite}
            canReveal={contactState.canReveal}
            onReveal={contactState.reveal}
            revealing={contactState.revealing}
            revealError={contactState.revealError}
            fallbackRegion={
              contactState.contact?.regionId && contactState.contact.regionName
                ? { id: contactState.contact.regionId, name: contactState.contact.regionName }
                : null
            }
            fallbackDistrict={
              contactState.contact?.districtId && contactState.contact.districtName
                ? { id: contactState.contact.districtId, name: contactState.contact.districtName }
                : null
            }
          />
        ) : null}
      </Form>
    </Modal>
  )
}
